import { useRef, useCallback } from 'preact/hooks';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { getKey } from '../utils/PRIVATE_collectionUtils';
import { useReorderableItem } from '../hooks/PRIVATE_useReorderable';
import { ReorderableItemProps } from '../UNSAFE_Collection/Reorderable.types';
import { useTabBarContext } from '../UNSAFE_TabBar';
import { styles } from './themes/ReorderableTabBarItemStyles.css';

export const ITEM_SELECTOR = '[role="tab"]';

type ReorderableTabBarItem<K extends string | number> = ReorderableItemProps<K> & {
  layout: 'stretch' | 'condense' | undefined;
};

/**
 * A private item that will be used by ReorderableTabBar, location TBD (could be moved to PRIVATE_TabBar)
 */
export function ReorderableTabBarItem<K extends string | number>({
  children,
  dragKey,
  setDragKey,
  onReorder,
  layout,
  rootRef
}: ReorderableTabBarItem<K>) {
  const tabBarItemRef = useRef<HTMLDivElement | null>(null);
  const getItem = useCallback(
    () => tabBarItemRef?.current?.querySelector(ITEM_SELECTOR) as HTMLElement,
    []
  );
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/tabbar-item-key', getKey(getItem()) + '');
    },
    [getItem]
  );

  const { currentKey } = useTabBarContext();
  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      currentKey: currentKey as K,
      isDisabled: onReorder ? false : true,
      draggable: onReorder ? true : false,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: tabBarItemRef,
      getItem,
      setTransferData,
      itemStyles: styles,
      itemSelector: ITEM_SELECTOR
    });

  //instead of specifying flex: '1 0 auto' in style
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  return (
    <div {...rootProps} style={flexDimensions}>
      {children}
      {!isDragged && dragKey && (
        <>
          <div {...overlayZoneBeforeProps}></div>
          <div {...overlayZoneAfterProps}></div>
        </>
      )}
    </div>
  );
}
